import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PoweredBy } from "@/components/PoweredBy";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export default function CabinAuth() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [cabin, setCabin] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("login"); // login | create | forgot | reset
  const [f, setF] = useState({});
  const [num, setNum] = useState("");
  const codeVal = () => cabin ? cabin.cabin_prefix + (/^\d+$/.test(num.trim()) && num.trim().length < 4 ? num.trim().padStart(4, "0") : num.trim()) : "";
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/cabin/${publicId}`)
      .then((r) => { setCabin(r.data); setMode(r.data.has_password ? "login" : "create"); })
      .catch(() => setNotFound(true));
  }, [publicId]);

  const finish = (data) => {
    localStorage.setItem("sf_cabin_token", data.token);
    localStorage.setItem("sf_cabin", JSON.stringify(data.cabin));
    navigate("/cabin-portal");
  };

  const doLogin = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/cabin/login`, { cabin_code: codeVal(), password: f.password }); finish(data); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const doCreate = async () => {
    if ((f.password || "").length < 8) return toast.error("Password must be at least 8 characters");
    if (f.password !== f.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/cabin/create-password`, { cabin_code: codeVal(), name: f.name, email: f.email, phone: f.phone, password: f.password }); toast.success("Password created"); finish(data); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const doForgot = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/cabin/forgot-password`, { cabin_code: codeVal() }); toast.success(`OTP sent to ${data.email_hint}`); setMode("reset"); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const doReset = async () => {
    if ((f.password || "").length < 8) return toast.error("Password must be at least 8 characters");
    if (f.password !== f.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try { const { data } = await axios.post(`${API}/public/cabin/reset-password`, { cabin_code: codeVal(), otp: f.otp, password: f.password }); toast.success("Password reset"); finish(data); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6"><div className="bg-white rounded-2xl border p-8 text-center max-w-sm"><h2 className="font-display text-xl font-bold text-navy-900">Cabin not available</h2><p className="text-slate-500 text-sm mt-2">This QR code is disabled or the cabin was not found. Please contact reception.</p></div></div>;
  if (!cabin) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="animate-spin text-navy-900" /></div>;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden sf-fade-up">
        <div className="bg-navy-900 px-6 py-5 text-center">
          {cabin.logo_url ? <CompanyLogo logoUrl={cabin.logo_url} className="h-9 mx-auto" light alt={cabin.company} /> : <p className="text-white font-display font-bold text-lg">{cabin.company}</p>}
          <p className="text-blue-100/70 text-xs mt-1">{cabin.branch} · Cabin Member Portal</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Cabin ID</p>
            <div className="flex items-center gap-1">
              <span className="font-mono font-bold text-navy-900" data-testid="cabin-code-display">{cabin.cabin_prefix}</span>
              <input value={num} onChange={(e) => setNum(e.target.value)} data-testid="cabin-num-input" inputMode="numeric" placeholder="0001"
                className="w-24 font-mono font-bold text-navy-900 bg-white border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {mode === "login" && (
            <>
              <div><Label>Password</Label><PasswordInput testId="cabin-login-password" className="mt-1 h-11" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
              <Button onClick={doLogin} disabled={busy} data-testid="cabin-login-btn" className="w-full h-11 bg-navy-900 hover:bg-navy-800 font-semibold">{busy ? <Loader2 className="animate-spin" size={16} /> : "Login"}</Button>
              <div className="flex justify-between text-sm">
                <button onClick={() => { setF({}); setMode("create"); }} className="text-blue-600 hover:underline" data-testid="cabin-create-link">Create Password</button>
                <button onClick={() => { setF({}); setMode("forgot"); }} className="text-slate-500 hover:underline" data-testid="cabin-forgot-link">Forgot Password?</button>
              </div>
            </>
          )}

          {mode === "create" && (
            <>
              <p className="text-sm text-slate-500">First time here? Set up your portal access.</p>
              <div><Label>Name *</Label><Input data-testid="cabin-create-name" className="mt-1 h-11" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div><Label>Email *</Label><Input type="email" data-testid="cabin-create-email" className="mt-1 h-11" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><Label>Mobile *</Label><Input data-testid="cabin-create-phone" className="mt-1 h-11" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              <div><Label>Password *</Label><PasswordInput testId="cabin-create-password" className="mt-1 h-11" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
              <div><Label>Confirm Password *</Label><PasswordInput testId="cabin-create-confirm" className="mt-1 h-11" value={f.confirm || ""} onChange={(e) => setF({ ...f, confirm: e.target.value })} /></div>
              <Button onClick={doCreate} disabled={busy} data-testid="cabin-create-btn" className="w-full h-11 bg-navy-900 hover:bg-navy-800 font-semibold">{busy ? <Loader2 className="animate-spin" size={16} /> : "Create Password"}</Button>
              {cabin.has_password && <button onClick={() => { setF({}); setMode("login"); }} className="text-blue-600 hover:underline text-sm w-full text-center">Back to Login</button>}
            </>
          )}

          {mode === "forgot" && (
            <>
              <p className="text-sm text-slate-500 flex items-center gap-1.5"><KeyRound size={14} /> We'll send a 6-digit code to your registered email.</p>
              <Button onClick={doForgot} disabled={busy} data-testid="cabin-sendotp-btn" className="w-full h-11 bg-navy-900 hover:bg-navy-800 font-semibold">{busy ? <Loader2 className="animate-spin" size={16} /> : "Send OTP"}</Button>
              <button onClick={() => setMode("login")} className="text-blue-600 hover:underline text-sm w-full text-center">Back to Login</button>
            </>
          )}

          {mode === "reset" && (
            <>
              <div><Label>OTP Code *</Label><Input data-testid="cabin-otp" className="mt-1 h-11 tracking-widest" maxLength={6} value={f.otp || ""} onChange={(e) => setF({ ...f, otp: e.target.value })} /></div>
              <div><Label>New Password *</Label><PasswordInput testId="cabin-reset-password" className="mt-1 h-11" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
              <div><Label>Confirm Password *</Label><PasswordInput testId="cabin-reset-confirm" className="mt-1 h-11" value={f.confirm || ""} onChange={(e) => setF({ ...f, confirm: e.target.value })} /></div>
              <Button onClick={doReset} disabled={busy} data-testid="cabin-reset-btn" className="w-full h-11 bg-navy-900 hover:bg-navy-800 font-semibold">{busy ? <Loader2 className="animate-spin" size={16} /> : "Reset Password"}</Button>
              <button onClick={doForgot} className="text-slate-500 hover:underline text-sm w-full text-center">Resend OTP</button>
            </>
          )}
        </div>
        <div className="bg-slate-50 py-3 flex justify-center border-t border-slate-100"><PoweredBy /></div>
      </div>
    </div>
  );
}
